/**
 * Hook Realtime Sécurisé
 * Gère les connexions WebSocket avec tous les garde-fous
 */

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { isFeatureEnabled, LIMITS } from '@/config/features'
import { realtimeCircuitBreaker } from '@/lib/circuitBreaker'
import { healthMonitor } from '@/lib/healthCheck'
import { connectionManager } from '@/lib/connectionManager'
import { logger } from '@/utils/productionLogger'

interface UseSecureRealtimeOptions {
  queryKey: readonly string[]
  table: string
  filter?: string
  userId?: string
  enabled?: boolean
}

/**
 * Hook pour écouter les changements Realtime avec protection
 */
export function useSecureRealtime({
  queryKey,
  table,
  filter,
  userId,
  enabled = true,
}: UseSecureRealtimeOptions) {
  const queryClient = useQueryClient()
  const channelRef = useRef<RealtimeChannel | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const invalidateTimeoutRef = useRef<NodeJS.Timeout>()
  
  // ✅ AUDIT 1: Générer un ID unique pour tracer cette instance du hook
  const hookInstanceId = useRef(crypto.randomUUID())
  
  // ✅ ÉTAPE 2: Stocker la clé utilisée au subscribe pour garantir la stabilité au cleanup
  const subscribedKeyRef = useRef<string | null>(null)
  
  // ✅ CORRECTION 1.1: Protection anti-race "async finishes after unmount"
  const cancelledRef = useRef(false)
  
  useEffect(() => {
    const instanceId = hookInstanceId.current
    
    // ✅ CORRECTION 1.1: Reset cancelled au début de l'effet
    cancelledRef.current = false
    
    // ✅ PHASE 1B ter: Log render en debug (pas info) pour traçabilité sans pollution
    logger.debug('[useSecureRealtime] Hook render', {
      instanceId,
      table,
      filter,
      queryKey,
      enabled,
      userId,
      userIdDefined: !!userId
    })
    
    // ✅ FIX CLEANUP: Définir cleanup AVANT les checks pour garantir nettoyage
    // Défini AVANT setupRealtime pour pouvoir être appelé depuis les callbacks
    const cleanup = (reason: string = 'unmount') => {
      // ✅ CORRECTION 1.1: Marquer comme cancelled pour éviter race condition
      cancelledRef.current = true
      
      const subscribedKey = subscribedKeyRef.current
      const currentChannelsSize = connectionManager.getStats().totalChannels
      
      logger.debug('[useSecureRealtime] UNSUBSCRIBE_START', {
        instanceId,
        table,
        filter,
        reason,
        subscribedKey,
        channelExists: !!channelRef.current,
        channelTopic: channelRef.current?.topic,
        channelsSize: currentChannelsSize
      })
      
      if (invalidateTimeoutRef.current) {
        clearTimeout(invalidateTimeoutRef.current)
      }
      
      if (channelRef.current && subscribedKey) {
        // ✅ ÉTAPE 2: Utiliser la clé stockée au subscribe (pas recalculée)
        const [keyTable, keyFilter] = subscribedKey.includes(':') 
          ? subscribedKey.split(':')
          : [subscribedKey, undefined]
        
        logger.debug('[useSecureRealtime] Calling releaseChannel', {
          instanceId,
          table: keyTable,
          filter: keyFilter,
          subscribedKey
        })
        
        connectionManager.releaseChannel(keyTable, keyFilter)
        channelRef.current = null
        subscribedKeyRef.current = null
        
        logger.debug('[useSecureRealtime] UNSUBSCRIBE_END', { 
          instanceId,
          table,
          reason,
          channelsSize: connectionManager.getStats().totalChannels
        })
      } else {
        logger.warn('[useSecureRealtime] UNSUBSCRIBE_SKIPPED', {
          instanceId,
          table,
          reason,
          channelExists: !!channelRef.current,
          subscribedKey
        })
      }
    }
    
    // Vérifier si enabled
    if (!enabled) {
      logger.debug('[useSecureRealtime] Disabled - no subscription', { 
        instanceId,
        table 
      })
      // ✅ FIX: Cleanup avant de sortir pour fermer connexions existantes
      return () => cleanup('disabled')
    }
    
    // Vérifier feature flag
    if (!isFeatureEnabled('REALTIME_ENABLED', userId)) {
      logger.debug('[useSecureRealtime] Feature disabled - no subscription', { 
        instanceId,
        table 
      })
      // ✅ FIX: Cleanup avant de sortir pour fermer connexions existantes
      return () => cleanup('feature_disabled')
    }
    
    // ✅ AUDIT 1: Reset si compteur de failed connections trop élevé (compteur obsolète)
    // On reset dès qu'on détecte un compteur > 10, même si des connexions sont actives
    const currentMetrics = healthMonitor.getMetrics()
    if (currentMetrics.failedConnections > 10) {
      logger.warn('[useSecureRealtime] Resetting stale failed connections counter', {
        instanceId,
        table,
        oldCount: currentMetrics.failedConnections,
        activeConnections: currentMetrics.realtimeConnections
      })
      healthMonitor.reset()
    }
    
    // Vérifier health
    const health = healthMonitor.checkHealth()
    if (!health.healthy) {
      logger.warn('[useSecureRealtime] Health check failed, skipping', {
        instanceId,
        table,
        issues: health.issues
      })
      // ✅ FIX: Cleanup avant de sortir
      return () => cleanup('health_check_failed')
    }
    
    // ✅ PHASE 1B ter: Logger SUBSCRIBE_START uniquement après tous les checks
    // = garantie qu'on va vraiment subscribe
    logger.debug('[useSecureRealtime] SUBSCRIBE_START', {
      instanceId,
      table,
      filter,
      queryKey,
      userId,
      userIdDefined: !!userId
    })
    
    // Setup Realtime avec circuit breaker
    const setupRealtime = async () => {
      return realtimeCircuitBreaker.execute(
        async () => {
          // ✅ ÉTAPE 2: Calculer et stocker la clé stable
          const channelKey = filter ? `${table}:${filter}` : table
          subscribedKeyRef.current = channelKey
          
          // ✅ AUDIT 2: Logger avant getOrCreateChannel
          logger.debug('[useSecureRealtime] Requesting channel', {
            instanceId,
            table,
            filter,
            channelKey,
            subscribedKeyStored: subscribedKeyRef.current
          })
          
          // Obtenir ou créer le channel via le manager
          const channel = connectionManager.getOrCreateChannel(table, filter)
          
          // ✅ CORRECTION 1.1: Vérifier si unmount pendant l'async
          if (cancelledRef.current) {
            logger.warn('[useSecureRealtime] Cancelled during async setup', {
              instanceId,
              table,
              filter
            })
            // Libérer immédiatement si channel créé
            if (channel) {
              connectionManager.releaseChannel(table, filter)
            }
            return null
          }
          
          if (!channel) {
            throw new Error('Failed to create channel (limit reached)')
          }
          
          channelRef.current = channel
          
          // ✅ AUDIT 3: Logger après obtention du channel
          logger.debug('[useSecureRealtime] Channel obtained', {
            instanceId,
            table,
            channelName: channel.topic,
            subscribedKey: subscribedKeyRef.current
          })
          
          // Configurer l'écoute des changements
          channel
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table,
                filter,
              },
              (payload) => {
                logger.debug('[useSecureRealtime] Change detected', {
                  table,
                  event: payload.eventType,
                })
                
                // Debounce les invalidations pour éviter les re-renders excessifs
                if (invalidateTimeoutRef.current) {
                  clearTimeout(invalidateTimeoutRef.current)
                }
                
                invalidateTimeoutRef.current = setTimeout(() => {
                  queryClient.invalidateQueries({ queryKey })
                  logger.debug('[useSecureRealtime] Cache invalidated', { queryKey })
                }, LIMITS.DEBOUNCE_DELAY)
              }
            )
            .subscribe((status) => {
              // ✅ Logger seulement les événements importants
              if (status === 'SUBSCRIBED') {
                reconnectAttemptsRef.current = 0
                logger.info('[useSecureRealtime] Successfully subscribed', {
                  instanceId,
                  table
                })
              }
              
              if (status === 'CLOSED') {
                logger.warn('[useSecureRealtime] Channel closed', {
                  instanceId,
                  table,
                  reconnectAttempts: reconnectAttemptsRef.current
                })
              }
              
              if (status === 'CHANNEL_ERROR') {
                healthMonitor.recordFailure()
                reconnectAttemptsRef.current++
                
                logger.error('[useSecureRealtime] Channel error', undefined, {
                  table,
                  attempts: reconnectAttemptsRef.current,
                  maxAttempts: LIMITS.MAX_RECONNECT_ATTEMPTS,
                  instanceId
                })
                
                if (reconnectAttemptsRef.current >= LIMITS.MAX_RECONNECT_ATTEMPTS) {
                  logger.error('[useSecureRealtime] Max reconnect attempts reached', undefined, {
                    table,
                    attempts: reconnectAttemptsRef.current,
                    instanceId
                  })
                  cleanup('max_reconnect_attempts')
                }
              }
            })
          
          return channel
        },
        () => {
          // Fallback : ne rien faire, le polling React Query prendra le relais
          logger.warn('[useSecureRealtime] Circuit breaker fallback', { table })
          return null
        }
      )
    }
    
    setupRealtime()
    
    return () => {
      logger.debug('[useSecureRealtime] Hook unmounting', {
        instanceId,
        table
      })
      cleanup('unmount')
    }
  }, [queryKey, table, filter, userId, enabled, queryClient])
}
